import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Button,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  Checkbox,
  useDisclosure,
  Spinner,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiShield, FiUsers, FiDatabase, FiActivity, FiZap } from 'react-icons/fi';

const API_BASE = '/api/v1/admin-ui';

function SuperAdmin() {
  const toast = useToast();
  
  // State
  const [superAdmins, setSuperAdmins] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [adminForm, setAdminForm] = useState({ username: '', email: '', password: '' });
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [rateLimits, setRateLimits] = useState([]);
  const [rateLimitsLoading, setRateLimitsLoading] = useState(false);
  const [clientIdInput, setClientIdInput] = useState('');
  const [specificRateLimit, setSpecificRateLimit] = useState(null);
  const [rateLimitFilter, setRateLimitFilter] = useState('all');
  const [rateLimitSummary, setRateLimitSummary] = useState(null);
  
  const { isOpen: isAdminModalOpen, onOpen: onAdminModalOpen, onClose: onAdminModalClose } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
    'X-Tenant-ID': localStorage.getItem('instance_id'),
  });

  // Fetch super admins and stats
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch super admins
      const adminsRes = await fetch(`${API_BASE}/super-admins/`, { headers: getAuthHeaders() });
      const adminsData = await adminsRes.json();
      if (adminsData.success) {
        setSuperAdmins(adminsData.admins || []);
      }

      // Fetch stats
      const statsRes = await fetch(`${API_BASE}/stats/`, { headers: getAuthHeaders() });
      const statsData = await statsRes.json();
      if (statsData.success) {
        setStats(statsData.stats);
      }
    } catch (e) {
      toast({ title: 'Error loading data', status: 'error', duration: 3000 });
    }
    setLoading(false);
  };

  const fetchRateLimits = async (filter) => {
    const activeFilter = filter !== undefined ? filter : rateLimitFilter;
    setRateLimitsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/rate-limits/?filter=${activeFilter}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setRateLimits(data.rate_limits || []);
        setRateLimitSummary(data.summary || null);
      } else {
        toast({ title: data.message || 'Error loading rate limits', status: 'warning', duration: 5000 });
        setRateLimits([]);
        setRateLimitSummary(null);
      }
    } catch (e) {
      toast({ title: 'Error loading rate limits', status: 'error', duration: 3000 });
    }
    setRateLimitsLoading(false);
  };

  const changeRateLimitFilter = (filter) => {
    setRateLimitFilter(filter);
    fetchRateLimits(filter);
  };

  const fetchSpecificRateLimit = async () => {
    if (!clientIdInput.trim()) {
      toast({ title: 'Please enter a client ID', status: 'warning', duration: 3000 });
      return;
    }
    
    setRateLimitsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/rate-limits/${encodeURIComponent(clientIdInput.trim())}/`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setSpecificRateLimit(data.rate_limit);
        toast({ title: 'Rate limit status loaded', status: 'success', duration: 3000 });
      } else {
        toast({ title: data.message || 'Error loading rate limit', status: 'error', duration: 3000 });
        setSpecificRateLimit(null);
      }
    } catch (e) {
      toast({ title: 'Error loading rate limit', status: 'error', duration: 3000 });
      setSpecificRateLimit(null);
    }
    setRateLimitsLoading(false);
  };

  const resetRateLimit = async (clientId) => {
    try {
      const res = await fetch(`${API_BASE}/rate-limits/${encodeURIComponent(clientId)}/reset/`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: data.message, status: 'success', duration: 3000 });
        fetchRateLimits(); // Refresh the list
      } else {
        toast({ title: data.message || 'Error resetting rate limit', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error resetting rate limit', status: 'error', duration: 3000 });
    }
  };

  // Admin Modal Handlers
  const openAddAdmin = () => {
    setEditingAdmin(null);
    setAdminForm({ username: '', email: '', password: '' });
    onAdminModalOpen();
  };

  const openEditAdmin = (admin) => {
    setEditingAdmin(admin);
    setAdminForm({ 
      username: admin.username || '', 
      email: admin.email || '', 
      password: '',
    });
    onAdminModalOpen();
  };

  const handleSaveAdmin = async () => {
    const method = editingAdmin ? 'PUT' : 'POST';
    const url = editingAdmin 
      ? `${API_BASE}/super-admins/${editingAdmin.id}/` 
      : `${API_BASE}/super-admins/`;
    
    const payload = { ...adminForm };
    if (!payload.password) delete payload.password;
    
    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: editingAdmin ? 'Admin updated' : 'Admin created', status: 'success', duration: 3000 });
        onAdminModalClose();
        fetchData();
      } else {
        toast({ title: data.message || 'Error saving admin', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving admin', status: 'error', duration: 3000 });
    }
  };

  // Delete admin
  const confirmDeleteAdmin = (admin) => {
    setAdminToDelete(admin);
    onDeleteModalOpen();
  };

  const handleDeleteAdmin = async () => {
    if (!adminToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/super-admins/${adminToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Admin removed', status: 'success', duration: 3000 });
        fetchData();
      } else {
        toast({ title: data.message || 'Error removing admin', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error removing admin', status: 'error', duration: 3000 });
    }
    onDeleteModalClose();
    setAdminToDelete(null);
  };

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" color="orange.500" />
        <Text mt={4}>Loading...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="lg" mb={6}>Super Admin</Heading>

      <Tabs colorScheme="orange">
        <TabList>
          <Tab><FiShield style={{ marginRight: 8 }} /> Overview</Tab>
          <Tab><FiUsers style={{ marginRight: 8 }} /> Super Admins</Tab>
          <Tab><FiZap style={{ marginRight: 8 }} /> Rate Limits</Tab>
        </TabList>

        <TabPanels>
          {/* Overview Tab */}
          <TabPanel px={0}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
              <Stat bg="white" p={6} borderRadius="lg" boxShadow="sm">
                <StatLabel color="gray.500">Total Users</StatLabel>
                <StatNumber color="orange.600">{stats?.total_users || 0}</StatNumber>
                <StatHelpText>In current instance</StatHelpText>
              </Stat>
              <Stat bg="white" p={6} borderRadius="lg" boxShadow="sm">
                <StatLabel color="gray.500">Active Profiles</StatLabel>
                <StatNumber color="green.600">{stats?.active_profiles || 0}</StatNumber>
                <StatHelpText>Active accounts</StatHelpText>
              </Stat>
              <Stat bg="white" p={6} borderRadius="lg" boxShadow="sm">
                <StatLabel color="gray.500">Super Admins</StatLabel>
                <StatNumber color="purple.600">{stats?.super_admins || superAdmins.length}</StatNumber>
                <StatHelpText>With full access</StatHelpText>
              </Stat>
              <Stat bg="white" p={6} borderRadius="lg" boxShadow="sm">
                <StatLabel color="gray.500">Profile Tags</StatLabel>
                <StatNumber color="blue.600">{stats?.profile_tags || 0}</StatNumber>
                <StatHelpText>Available tags</StatHelpText>
              </Stat>
            </SimpleGrid>

            <Alert status="info" borderRadius="lg">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Instance: {localStorage.getItem('instance_name')}</Text>
                <Text fontSize="sm">You are managing data for this specific instance.</Text>
              </Box>
            </Alert>
          </TabPanel>

          {/* Super Admins Tab */}
          <TabPanel px={0}>
            <HStack justify="space-between" mb={6}>
              <Text color="gray.600">
                Super Admins have full access to manage all users and settings in this instance.
              </Text>
              <Button leftIcon={<FiPlus />} colorScheme="orange" onClick={openAddAdmin}>
                Add Super Admin
              </Button>
            </HStack>

            <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
              <Table>
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Username</Th>
                    <Th>Email</Th>
                    <Th>Status</Th>
                    <Th width="120px">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {superAdmins.map(admin => (
                    <Tr key={admin.id}>
                      <Td>
                        <HStack>
                          <FiShield color="var(--chakra-colors-purple-500)" />
                          <Text fontWeight="medium">{admin.username}</Text>
                        </HStack>
                      </Td>
                      <Td>{admin.email || '-'}</Td>
                      <Td>
                        <Badge colorScheme={admin.is_active ? 'green' : 'gray'}>
                          {admin.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <IconButton
                            icon={<FiEdit2 />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => openEditAdmin(admin)}
                            aria-label="Edit admin"
                          />
                          <IconButton
                            icon={<FiTrash2 />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => confirmDeleteAdmin(admin)}
                            aria-label="Remove admin"
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                  {superAdmins.length === 0 && (
                    <Tr>
                      <Td colSpan={4} textAlign="center" py={8}>
                        <Text color="gray.500">No super admins found</Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          </TabPanel>

          {/* Rate Limits Tab */}
          <TabPanel px={0}>
            <VStack spacing={6} align="stretch">
              <HStack justify="space-between" align="center">
                <Text color="gray.600">
                  Monitor and manage API rate limits for users and IP addresses.
                </Text>
                <Button leftIcon={<FiActivity />} colorScheme="blue" onClick={() => fetchRateLimits()} isLoading={rateLimitsLoading}>
                  Refresh All
                </Button>
              </HStack>

              {/* Summary Banner */}
              {rateLimitSummary && (
                <HStack spacing={4} flexWrap="wrap">
                  <Box bg="blue.50" px={4} py={2} borderRadius="md">
                    <Text fontSize="sm" color="blue.700">
                      <strong>{rateLimitSummary.total_active}</strong> active clients
                    </Text>
                  </Box>
                  <Box bg={rateLimitSummary.total_limited > 0 ? 'red.50' : 'green.50'} px={4} py={2} borderRadius="md">
                    <Text fontSize="sm" color={rateLimitSummary.total_limited > 0 ? 'red.700' : 'green.700'}>
                      <strong>{rateLimitSummary.total_limited}</strong> currently limited
                    </Text>
                  </Box>
                  <Box bg="purple.50" px={4} py={2} borderRadius="md">
                    <Text fontSize="sm" color="purple.700">
                      <strong>{rateLimitSummary.total_user}</strong> users
                    </Text>
                  </Box>
                  <Box bg="cyan.50" px={4} py={2} borderRadius="md">
                    <Text fontSize="sm" color="cyan.700">
                      <strong>{rateLimitSummary.total_ip}</strong> IPs
                    </Text>
                  </Box>
                </HStack>
              )}

              {/* Filter Buttons */}
              <HStack spacing={2}>
                <Text fontSize="sm" color="gray.500" mr={1}>Filter:</Text>
                {['all', 'limited', 'ip', 'user'].map(f => (
                  <Button
                    key={f}
                    size="sm"
                    variant={rateLimitFilter === f ? 'solid' : 'outline'}
                    colorScheme={f === 'limited' ? 'red' : f === 'ip' ? 'blue' : f === 'user' ? 'purple' : 'gray'}
                    onClick={() => changeRateLimitFilter(f)}
                  >
                    {f === 'all' ? 'All' : f === 'limited' ? '⛔ Currently Limited' : f === 'ip' ? '🌐 IPs Only' : '👤 Users Only'}
                  </Button>
                ))}
              </HStack>

              {/* Specific Client Lookup */}
              <Box bg="gray.50" p={4} borderRadius="lg">
                <Text fontWeight="medium" mb={3}>Check Specific Client</Text>
                <HStack>
                  <Input
                    placeholder="Enter client ID (e.g., user:123 or ip:192.168.1.1)"
                    value={clientIdInput}
                    onChange={(e) => setClientIdInput(e.target.value)}
                    focusBorderColor="blue.500"
                  />
                  <Button colorScheme="blue" onClick={fetchSpecificRateLimit} isLoading={rateLimitsLoading}>
                    Check
                  </Button>
                </HStack>
                {specificRateLimit && (
                  <Box mt={4} p={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                    <HStack justify="space-between" align="start">
                      <VStack align="start" spacing={2}>
                        <HStack>
                          <Badge colorScheme={specificRateLimit.client_type === 'user' ? 'purple' : 'blue'}>
                            {specificRateLimit.client_type}
                          </Badge>
                          <Text fontWeight="medium">{specificRateLimit.client_value}</Text>
                        </HStack>
                        <HStack spacing={6}>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm" fontWeight="medium">Minute Limit</Text>
                            <Text fontSize="sm">{specificRateLimit.minute.requests}/{specificRateLimit.minute.limit}</Text>
                            <Text fontSize="xs" color={specificRateLimit.minute.is_limited ? 'red.500' : 'green.500'}>
                              {specificRateLimit.minute.is_limited ? 'Limited' : 'OK'} • Reset in {specificRateLimit.minute.reset_in}s
                            </Text>
                          </VStack>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm" fontWeight="medium">Hour Limit</Text>
                            <Text fontSize="sm">{specificRateLimit.hour.requests}/{specificRateLimit.hour.limit}</Text>
                            <Text fontSize="xs" color={specificRateLimit.hour.is_limited ? 'red.500' : 'green.500'}>
                              {specificRateLimit.hour.is_limited ? 'Limited' : 'OK'} • Reset in {specificRateLimit.hour.reset_in}s
                            </Text>
                          </VStack>
                        </HStack>
                      </VStack>
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => resetRateLimit(specificRateLimit.client_id)}
                      >
                        Reset
                      </Button>
                    </HStack>
                  </Box>
                )}
              </Box>

              {/* All Rate Limits Table */}
              <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
                <Table>
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Client</Th>
                      <Th>Type</Th>
                      <Th>Minute Usage</Th>
                      <Th>Hour Usage</Th>
                      <Th width="120px">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rateLimits.map(limit => (
                      <Tr key={limit.client_id} bg={limit.is_limited ? 'red.50' : undefined}>
                        <Td>
                          <HStack>
                            {limit.is_limited && <Text fontSize="xs">⛔</Text>}
                            <Text fontWeight="medium">{limit.client_value}</Text>
                          </HStack>
                        </Td>
                        <Td>
                          <Badge colorScheme={limit.client_type === 'user' ? 'purple' : 'blue'}>
                            {limit.client_type}
                          </Badge>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm">{limit.minute.requests}/{limit.minute.limit}</Text>
                            <Text fontSize="xs" color={limit.minute.is_limited ? 'red.500' : 'gray.500'}>
                              {limit.minute.is_limited ? '⛔ Limited' : 'OK'} • Reset in {limit.minute.reset_in}s
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm">{limit.hour.requests}/{limit.hour.limit}</Text>
                            <Text fontSize="xs" color={limit.hour.is_limited ? 'red.500' : 'gray.500'}>
                              {limit.hour.is_limited ? '⛔ Limited' : 'OK'} • Reset in {limit.hour.reset_in}s
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Button
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => resetRateLimit(limit.client_id)}
                          >
                            Reset
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                    {rateLimits.length === 0 && !rateLimitsLoading && (
                      <Tr>
                        <Td colSpan={5} textAlign="center" py={8}>
                          <Text color="gray.500">No active rate limits found</Text>
                        </Td>
                      </Tr>
                    )}
                    {rateLimitsLoading && (
                      <Tr>
                        <Td colSpan={5} textAlign="center" py={8}>
                          <Spinner size="sm" mr={2} />
                          Loading rate limits...
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Admin Modal */}
      <Modal isOpen={isAdminModalOpen} onClose={onAdminModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingAdmin ? 'Edit Super Admin' : 'Add Super Admin'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Username</FormLabel>
                <Input
                  value={adminForm.username}
                  onChange={(e) => setAdminForm({ ...adminForm, username: e.target.value })}
                  focusBorderColor="orange.500"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={adminForm.email}
                  onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                  focusBorderColor="orange.500"
                />
              </FormControl>
              {!editingAdmin && (
                <FormControl isRequired>
                  <FormLabel>Password</FormLabel>
                  <Input
                    type="password"
                    value={adminForm.password}
                    onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                    focusBorderColor="orange.500"
                  />
                </FormControl>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAdminModalClose}>Cancel</Button>
            <Button colorScheme="orange" onClick={handleSaveAdmin}>
              {editingAdmin ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Remove Super Admin</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              Are you sure you want to remove <strong>{adminToDelete?.username}</strong> as a Super Admin?
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDeleteAdmin}>Remove</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default SuperAdmin;
